import { EventEmitter, Injector, NgZone } from '@angular/core';
import { Observable, Subscription, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { fabric } from 'fabric';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { ItemModel } from '../../../../models/itemModel';
import { FrameItem } from '../../../../models/session';
import { FormulaComponent } from '../../formula/formula.component';
import { MyscriptComponent } from '../../../../ui/myscript/myscript.component';
import { scaleEvent } from '../../../../services/session-rtd.service';
import { PlaceFormulaSnackbarComponent } from '../../../../ui/place-formula-snackbar/place-formula-snackbar.component';
import { FormulaConfigurations } from './board-formula-configurations';
import { CreationContext } from './board-item-configurations';
import { BoardItemComponentClass, FabricControls } from './board.item';

const FormulaInvisibleControls = Object.values(FabricControls);

export class BoardFormula extends BoardItemComponentClass<FormulaConfigurations, FormulaComponent>(
  fabric.Rect,
) {
  AngularComponentClass = FormulaComponent;

  private readonly dialog: MatDialog;

  private ngZone: NgZone;
  private snackBar: MatSnackBar;

  private boundEndPreview = this.endPreviewMode.bind(this);
  private canvasMouseMoveHandler = this.onCanvasMouseMove.bind(this);

  public shouldStartInPreviewMode = false;

  private contentSizeSubscription?: Subscription;

  private placementSnackbar?: MatSnackBarRef<PlaceFormulaSnackbarComponent>;

  public onPreviewEnded = new EventEmitter<void>();

  constructor(
    injector: Injector,
    configurations: FormulaConfigurations,
    creationContext: CreationContext,
  ) {
    super(injector, ItemModel.Formula, configurations, FormulaInvisibleControls, creationContext);

    this.dialog = injector.get<MatDialog>(MatDialog);
    this.ngZone = injector.get<NgZone>(NgZone);
    this.snackBar = injector.get<MatSnackBar>(MatSnackBar);
  }

  protected override onFabricAdded() {
    super.onFabricAdded();

    if (this.shouldStartInPreviewMode) {
      this.startPreviewMode();
    }
  }

  protected override onFabricRemoved() {
    super.onFabricRemoved();
    this.unregisterPreviewListeners();
    this.contentSizeSubscription?.unsubscribe();
  }

  bindAngularComponentOutputs() {
    super.bindAngularComponentOutputs();

    if (!this.componentRef) {
      return;
    }

    // The size of the BoardFormula should be based only on the content size
    this.contentSizeSubscription?.unsubscribe();
    this.contentSizeSubscription = this.componentRef.instance.contentSizeChanged.subscribe(
      ({ width, height }) => {
        if (!this.isOnStaticCanvas()) {
          this.set({ width, height } as Partial<this>);
          this.canvas?.requestRenderAll();
          this.syncHtmlWithFabric();
        }
      },
    );
  }

  getZIndexObservable$(): Observable<number> {
    return this.getDefaultZIndexObservable$();
  }

  override toFrameItem(): FrameItem {
    const encodedBoardItem = super.toFrameItem();
    return {
      ...encodedBoardItem,
      model: ItemModel.Formula,
      equation_state: {
        equation_data: this.configurations.equation,
      },
      position: new Map([
        ...Array.from(encodedBoardItem.position?.entries() ?? []),
        ['font:size', `${this.configurations.fontSize}`],
        ['font:color', this.configurations.fontColor],
        ['background:color', this.configurations.backgroundColor],
      ]),
    };
  }

  override openLock() {
    // Users can only move formulas, no rotation or scaling manually
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = true;
    this.lockScalingX = true;
    this.lockScalingY = true;
    this.lockUniScaling = true;
    this.lockSkewingX = true;
    this.lockSkewingY = true;
    this.lockScalingFlip = true;
  }

  // When the formula is first added, we insert it in the preview mode
  // The preview mode is when the formula has a low opacity, and the formula follows the mouse until the user clicks on the board to position it.
  public startPreviewMode() {
    this.registerPreviewListeners();

    this.showPlaceFormulaSnackBar();

    this.componentRef?.setInput('opacity', 0.5);
    this.componentRef?.changeDetectorRef.detectChanges();
  }

  public endPreviewMode() {
    this.unregisterPreviewListeners();

    this.placementSnackbar?.dismiss();

    this.componentRef?.setInput('opacity', 1);
    this.componentRef?.changeDetectorRef.detectChanges();

    // To sync the final position with the server
    this.syncChangesWithServer();

    this.onPreviewEnded.emit();

    this.setCoords(); // Necessary to make the fabric object behaves well after changing its position
    // without it, added formulas won't be interactable until the coords are set
  }

  private registerPreviewListeners() {
    if (this.isOnStaticCanvas()) {
      return;
    }

    this.domListener.add(document, 'click', this.boundEndPreview, true);
    this.domListener.add(document, 'touchstart', this.boundEndPreview, true);

    this.canvas?.on('mouse:move', this.canvasMouseMoveHandler);
  }

  private unregisterPreviewListeners() {
    this.domListener.clear();

    this.canvas?.off('mouse:move', this.canvasMouseMoveHandler);
  }

  protected onCanvasMouseMove(e: fabric.IEvent) {
    e = scaleEvent(e);
    const pointer = e.pointer!;

    this.set({
      left: pointer.x - (this.width ?? 0) / 2,
      top: pointer.y - (this.height ?? 0) / 2,
    } as Partial<this>);
    this.syncHtmlWithFabric();
  }

  public openFormulaEditDialog(): void {
    const dialogRef = this.dialog.open(MyscriptComponent, {
      width: '800px',
      panelClass: 'math-quill-dialog-container',
      data: { katex: this.configurations.equation.slice(2, -2) },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.changeState({ equation: `$$${result}$$` });
        }
      });
  }

  showPlaceFormulaSnackBar() {
    this.ngZone.runOutsideAngular(() => {
      if (this.snackBar._openedSnackBarRef) {
        this.snackBar.dismiss();
        this.snackBar._openedSnackBarRef.afterDismissed();
      }
      this.placementSnackbar = this.snackBar.openFromComponent(PlaceFormulaSnackbarComponent, {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: 'lock-snack-bar',
      });
    });
  }
}
